import React from "react";
import styled from "styled-components";
import HeartDucky from "images/download/HeartDucky";
import { StaticImage } from "gatsby-plugin-image";
import AuthIcon from "components/AuthIcon";
import { AppStoreButton, PlayStoreButton } from "components/DownloadButtons";
import { DownloadURL } from "constants/download";
import { URLwithUTMSuffix } from "utils/download";
import eDarkIcon from "components/eDarkIcon";

interface IProps {
    background: string;
    heading: string;
    subheading: string;
    icon: string;
    language: string;
}

const Container = styled.section.attrs({
    className: "container",
})`
    margin-top: 8%;
    margin-bottom: 8%;
`;

const HeartDuckyContainer = styled.div.attrs({
    className: "d-flex justify-content-end",
})`
    margin-bottom: -250px;

    @media (min-width: 992px) {
        margin-bottom: -300px;
    }
`;

const ExperienceEnteContainer = styled.div<{ background: string }>`
    border-radius: 50px;
    background: ${(props) => props.background};
    position: relative;
    padding: 30px;
    align-items: center;

    @media (min-width: 992px) {
        padding: 0 100px;
    }
`;

const Ente = styled(eDarkIcon)`
    position: absolute;
    top: -530px;
    left: 30px;

    @media (min-width: 992px) {
        left: 100px;
    }
`;

const Auth = styled(AuthIcon)`
    position: absolute;
    top: -100px;
    left: 30px;

    @media (min-width: 992px) {
        left: 90px;
    }
`;

const Heading = styled.h1<{ isAuthProp: boolean }>`
    color: ${(props) => (props.isAuthProp ? "#fff" : "#fff")};
    font-size: 36px;
    font-style: normal;
    font-weight: 800;
    line-height: 130%;
    text-align: left;
    margin-bottom: 50px;

    @media (min-width: 992px) {
        font-size: 40px;
        margin-bottom: 0px;
    }
`;

const SubHeading = styled.p<{ isAuthProp: boolean }>`
    margin-bottom: 50px;
    color: ${(props) => (props.isAuthProp ? "#fff" : "rgb(0,0,0,0.5)")};
    font-size: 17px;
    font-weight: 600;
    margin-top: 0;

    @media (min-width: 992px) {
        font-size: 20px;
    }
`;

const QrContainer = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 20px;
    height: 180px;
    width: 180px;
    margin-bottom: 20px;
`;

export default function ExperienceEnte(props: IProps) {
    const isAuthProp = props.icon === "auth";
    const playStoreLink = isAuthProp
        ? URLwithUTMSuffix(DownloadURL.authGooglePlay)
        : URLwithUTMSuffix(DownloadURL.googlePlay);
    const appStoreLink = isAuthProp
        ? URLwithUTMSuffix(DownloadURL.authAppStore)
        : URLwithUTMSuffix(DownloadURL.appStore);

    let pageIDForCTA = "";

    if (isAuthProp) {
        pageIDForCTA = "auth";
    } else {
        pageIDForCTA = "photos";
    }

    return (
        <Container>
            <HeartDuckyContainer>
                {isAuthProp ? (
                    <HeartDucky fillColor="#bf6cf0" />
                ) : (
                    <HeartDucky fillColor="var(--color-green)" />
                )}
            </HeartDuckyContainer>
            <ExperienceEnteContainer background={props.background}>
                {isAuthProp ? <Auth width="240px" /> : <Ente width="140px" />}
                <div className="row">
                    <div className="col-lg-4 col-xl-6 d-flex flex-column justify-content-center">
                        <Heading isAuthProp={isAuthProp}>
                            {props.heading}
                        </Heading>
                        <SubHeading
                            isAuthProp={isAuthProp}
                            className="d-none d-lg-flex"
                        >
                            {props.subheading}
                        </SubHeading>
                    </div>
                    <div className="col-sm-6 col-lg-4 col-xl-3 d-flex flex-column align-items-lg-center p-lg-4">
                        <QrContainer className="d-none d-lg-flex">
                            {isAuthProp ? (
                                <StaticImage
                                    src="../../images/download/play-store-auth-qr.png"
                                    width={160}
                                    height={160}
                                    alt="Download on Google Playstore"
                                    objectFit="contain"
                                    placeholder="none"
                                    loading="lazy"
                                />
                            ) : (
                                <StaticImage
                                    src="../../images/download/play-store-qr.png"
                                    width={160}
                                    height={160}
                                    alt="Download on Google Playstore"
                                    objectFit="contain"
                                    placeholder="none"
                                    loading="lazy"
                                />
                            )}
                        </QrContainer>
                        <PlayStoreButton
                            source={`${pageIDForCTA}-experience`}
                            link={playStoreLink}
                            lang={props.language}
                            width={197}
                            height={59}
                        />
                    </div>
                    <div className="col-sm-6 col-lg-4 col-xl-3 d-flex flex-column align-items-lg-center p-lg-4">
                        <QrContainer className="d-none d-lg-flex">
                            {isAuthProp ? (
                                <StaticImage
                                    src="../../images/download/app-store-auth-qr.png"
                                    width={160}
                                    height={160}
                                    alt="Download on AppStore"
                                    objectFit="contain"
                                    placeholder="none"
                                    loading="lazy"
                                />
                            ) : (
                                <StaticImage
                                    src="../../images/download/app-store-qr.png"
                                    width={160}
                                    height={160}
                                    alt="Download on AppStore"
                                    objectFit="contain"
                                    placeholder="none"
                                    loading="lazy"
                                />
                            )}
                        </QrContainer>
                        <AppStoreButton
                            source={`${pageIDForCTA}-experience`}
                            link={appStoreLink}
                            lang={props.language}
                            width={197}
                            height={64}
                        />
                    </div>
                </div>
            </ExperienceEnteContainer>
        </Container>
    );
}
